/**
 * Module Santé — primitives UI partagées.
 *
 * Couleurs et helpers réutilisés par les 5 écrans du module (Aujourd'hui,
 * Tendances, Dossier médical, Coach, Réglages). Conforme aux principes UX
 * du CDC §4.1 : charge cognitive faible, langage non médicalisant.
 */
import React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'

export const HEALTH_ACCENT = '#3ddc97'
export const PANEL_BG = 'rgba(255,255,255,0.04)'
export const PAGE_BG = '#00001d'

export const cardSx = {
  bgcolor: PANEL_BG,
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  p: 2.5,
}

/**
 * Style à appliquer aux champs MUI (TextField/Select) posés sur le fond
 * sombre du module. Le thème global de Life App est clair : sans ça, le
 * texte et les bordures sont quasi invisibles sur le fond #00001d.
 */
export const darkFieldSx = {
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.4)',
    opacity: 1,
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: HEALTH_ACCENT },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.25)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.45)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: HEALTH_ACCENT,
  },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
}

// Libellés humains des métriques santé normalisées (§5.2).
export const METRIC_LABELS = {
  steps: 'Pas',
  active_energy: 'Énergie active',
  exercise_minutes: 'Minutes d’exercice',
  distance: 'Distance',
  sleep_duration: 'Sommeil',
  sleep_efficiency: 'Efficacité du sommeil',
  heart_rate: 'Fréquence cardiaque',
  resting_heart_rate: 'FC au repos',
  hrv: 'Variabilité cardiaque',
  spo2: 'SpO₂',
  systolic_bp: 'Tension systolique',
  diastolic_bp: 'Tension diastolique',
  weight: 'Poids',
  bmi: 'IMC',
  body_fat: 'Masse grasse',
  glucose: 'Glycémie',
  mood: 'Humeur',
  stress: 'Stress',
  hydration: 'Hydratation',
  calories_intake: 'Calories',
}

export const metricLabel = (m) => METRIC_LABELS[m] || m

export const AXIS_LABELS = {
  sleep: 'Sommeil',
  activity: 'Activité',
  mental: 'Mental',
  nutrition: 'Nutrition',
  biometrics: 'Biométrie',
}

export const severityColor = (severity) =>
  ({ critical: '#ff5d6c', warning: '#ffb454', info: '#5ab9ff' }[severity] ||
  '#5ab9ff')

export function Loading({ label = 'Chargement…' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
      <CircularProgress sx={{ color: HEALTH_ACCENT }} />
      <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{label}</Typography>
    </Box>
  )
}

export function SectionTitle({ children, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
        {children}
      </Typography>
      {action}
    </Box>
  )
}

export function EmptyState({ children }) {
  return (
    <Box
      sx={{
        ...cardSx,
        textAlign: 'center',
        color: 'rgba(255,255,255,0.55)',
        py: 4,
      }}
    >
      <Typography sx={{ fontSize: 14 }}>{children}</Typography>
    </Box>
  )
}
